import React from 'react';
import { Alert, Box, Button, Chip, Link, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import {
  AutoFixHigh as AutoFixHighIcon,
  Publish as PublishIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import type { Schema } from '../../services/api';
import type { AnnotationMode } from '../../services/schemaApi';
import EnrichmentLogsTab from './EnrichmentLogsTab';
import EnrichmentSummaryBanner from './EnrichmentSummaryBanner';
import UnifiedSchemaBrowser from '../schema-view/UnifiedSchemaBrowser';
import { useFeatureFlags } from '../../context/FeatureFlagContext';
import { FeatureName } from '../../types/featureNames';

interface SchemaDraftDetailViewProps {
  draft: Schema;
  onClose: () => void;
  onAnnotate: (mode?: AnnotationMode) => void;
  onPromote: () => void;
  onEdit: () => void;
}

/**
 * Body of the draft Schema Details drawer. Action buttons + the
 * template summary / enrichment banner at the top, then the unified
 * schema browser (hierarchy tree + level detail + inline catalogs).
 *
 * The drawer shell (right-anchored, ~92% viewport) is managed by the
 * caller via `<SchemaDetailDrawer>`.
 */
const SchemaDraftDetailView: React.FC<SchemaDraftDetailViewProps> = ({
  draft,
  onAnnotate,
  onPromote,
  onEdit,
}) => {
  const { hasFeature } = useFeatureFlags();
  const canViewLogs = hasFeature(FeatureName.SHOW_LOGS);
  const definition = draft.definition || {};
  const needsInterpretation = !!definition.needs_interpretation;
  const hasOutputStructure = !!definition.output_structure;
  const hasTables = !!definition.tables && Object.keys(definition.tables).length > 0;
  const isAnnotated = hasOutputStructure || hasTables;
  const templateSummary = definition.template_summary;
  const enrichmentMeta = (draft.metadata as any) || {};

  const [tabValue, setTabValue] = React.useState(0);

  // Primary action label adapts to draft state.
  // - Fresh template (needs interpretation) → "Annotate Template" (warning)
  // - Already annotated → "Re-annotate Schema" (outlined)
  // - Otherwise (e.g. uploaded SQL schema with tables but no template) → "Annotate Schema"
  const primaryLabel = needsInterpretation
    ? 'Annotate Template'
    : isAnnotated
      ? 'Re-annotate Schema'
      : 'Annotate Schema';

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action row */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
            Edit schema
          </Button>
          <Button
            size="small"
            variant="contained"
            color={needsInterpretation ? 'warning' : 'primary'}
            startIcon={<AutoFixHighIcon />}
            onClick={() => onAnnotate('full')}
          >
            {primaryLabel}
          </Button>
          <Button size="small" variant="outlined" startIcon={<PublishIcon />} onClick={onPromote}>
            Promote to Schema
          </Button>
        </Stack>
      </Box>

      {/* Template summary chip strip */}
      {templateSummary && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                Template:
              </Typography>
              {templateSummary.xsd_metadata ? (
                <Chip label="XSD" size="small" color="info" variant="outlined" />
              ) : (
                <Chip label={`${(templateSummary.format || '').toUpperCase()}`} size="small" variant="outlined" />
              )}
              <Chip label={`Depth: ${templateSummary.depth || 0}`} size="small" variant="outlined" />
              <Chip label={`${templateSummary.element_count || 0} elements`} size="small" variant="outlined" />
              <Chip label={`${templateSummary.unique_elements || 0} unique`} size="small" variant="outlined" />
              {templateSummary.xsd_metadata?.groups?.length > 0 && (
                <Chip label={`${templateSummary.xsd_metadata.groups.length} groups`} size="small" variant="outlined" color="info" />
              )}
              {templateSummary.xsd_metadata?.attribute_groups?.length > 0 && (
                <Chip label={`${templateSummary.xsd_metadata.attribute_groups.length} attr groups`} size="small" variant="outlined" color="info" />
              )}
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Unresolved XSD imports warning */}
      {(() => {
        const resolution = templateSummary?.xsd_metadata?.resolution;
        const unresolved = resolution?.unresolved_imports;
        if (!unresolved || unresolved.length === 0) return null;
        return (
          <Box sx={{ px: 2, pt: 1 }}>
            <Alert severity="warning">
              <strong>
                {unresolved.length} referenced schema{unresolved.length > 1 ? 's' : ''} not uploaded:
              </strong>{' '}
              {unresolved.join(', ')}. Upload them alongside the main XSD for richer type
              information, enumerations, and validation.
              {resolution.resolved_imports?.length > 0 && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Resolved: {resolution.resolved_imports.join(', ')}
                  {resolution.resolved_type_count > 0 &&
                    ` (${resolution.resolved_type_count} types, ${resolution.resolved_enum_count} enumerations)`}
                </Typography>
              )}
            </Alert>
          </Box>
        );
      })()}

      {needsInterpretation && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Alert severity="info">
            This template hasn't been annotated yet. Click <strong>Annotate Template</strong> to
            have the AI agent derive tables, columns, the output hierarchy, descriptions, and
            validation rules.
          </Alert>
        </Box>
      )}

      {/* Enrichment summary banner */}
      <Box sx={{ px: 2, pt: 1 }}>
        <EnrichmentSummaryBanner
          metadata={enrichmentMeta}
          onRetry={() => onAnnotate('descriptions')}
          disabled={needsInterpretation}
        />
      </Box>

      {/* Body: unified schema browser (+ optional enrichment logs tab) */}
      {canViewLogs ? (
        <>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ px: 2 }}>
            <Tab label="Schema" />
            <Tab label="Enrichment Logs" />
          </Tabs>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {tabValue === 0 && <UnifiedSchemaBrowser definition={definition as any} />}
            {tabValue === 1 && <EnrichmentLogsTab draftId={draft.schema_id} />}
          </Box>
        </>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <UnifiedSchemaBrowser definition={definition as any} />
        </Box>
      )}
    </Box>
  );
};

export default SchemaDraftDetailView;
